<template>
  <v-card class="pa-4">
    <div class="mb-4">
      <router-link to="/resources/conferences" class="text-decoration-none">
        <v-icon small class="mr-1">arrow_back</v-icon>
        Back to Conferences
      </router-link>
    </div>

    <div class="text-center mb-4">
      <v-img
        :src="asmImage"
        max-width="500"
        class="mx-auto"
        style="border-radius: 8px;"
      ></v-img>
    </div>

    <p class="home-section-title text-center">ASM Microbe 2026</p>
    <p class="text-center subtitle-1 mb-6">
      <v-icon class="mr-2">date_range</v-icon>June 4-7, 2026
      <v-icon class="ml-4 mr-2">place</v-icon>Washington, D.C.
    </p>

    <v-divider class="my-6"></v-divider>

    <div class="mb-6">
      <h2 class="headline mb-4">BugSeq Events at ASM Microbe 2026</h2>

      <v-card class="mb-4 pa-4" outlined>
        <h3 class="title mb-2">
          <v-icon class="mr-2" color="primary">store</v-icon>
          BugSeq Booth #2206
        </h3>
        <p class="mb-2">Visit us at our booth to learn about BugSeq's microbial bioinformatics platform.</p>
        <div class="ml-8">
          <div class="mb-1"><strong>Friday, June 5:</strong> 10:00 AM – 5:00 PM</div>
          <div class="mb-1"><strong>Saturday, June 6:</strong> 10:00 AM – 5:00 PM</div>
          <div class="mb-1"><strong>Sunday, June 7:</strong> 10:00 AM – 4:00 PM</div>
        </div>
      </v-card>

      <v-card class="mb-4 pa-4" outlined>
        <h3 class="title mb-2">
          <v-icon class="mr-2" color="primary">mic</v-icon>
          Sherif Nour Presenting at ONT Booth #2015
        </h3>
        <p class="mb-3 font-weight-medium">"EPI2ME & BugSeq: Microbial Bioinformatics for WGS, Metagenomics and 16S/ITS"</p>
        <div class="ml-8">
          <div class="mb-1"><strong>Friday, June 5:</strong> 1:00 PM</div>
          <div class="mb-1"><strong>Saturday, June 6:</strong> 3:30 PM</div>
          <div class="mb-1"><strong>Sunday, June 7:</strong> 11:00 AM</div>
        </div>
      </v-card>
    </div>

    <v-divider class="my-6"></v-divider>

    <div class="mb-6">
      <h2 class="headline mb-4">Poster Presentations from BugSeq Users</h2>
      <p class="mb-4">We're proud to highlight the work of our users presenting at ASM Microbe 2026:</p>

      <v-card
        v-for="(presentation, index) in presentations"
        :key="index"
        class="mb-4 pa-4"
        outlined
      >
        <h3 class="subtitle-1 font-weight-bold mb-2">{{ presentation.title }}</h3>
        <div class="body-2 mb-2">
          <v-chip small class="mr-2 mb-2">{{ presentation.type }}</v-chip>
          <v-icon small class="mr-1">person</v-icon>
          <strong>{{ presentation.presenter }}</strong>, {{ presentation.organisation }}
        </div>
        <div class="body-2">
          <v-icon small class="mr-1">schedule</v-icon>
          {{ presentation.formattedDateTime }}
        </div>
      </v-card>
    </div>

    <v-divider class="my-6"></v-divider>

    <div class="text-center">
      <p class="title mb-4">
        Interested in meeting with us at ASM Microbe 2026?
      </p>
      <v-btn
        color="primary"
        href="/#contact"
        large
      >
        <v-icon left>email</v-icon>
        Contact Us
      </v-btn>
    </div>
  </v-card>
</template>

<script>
import ImgASM from "@bugseq-site/www/src/assets/img/resources/conferences/2026/asm-microbe.webp";

export default {
  metaInfo: {
    title: "ASM Microbe 2026",
    titleTemplate: "BugSeq | %s",
  },
  data() {
    return {
      asmImage: ImgASM,
      presentations: [
        {
          title: "Rapid and Routine 24-Hour Antimicrobial Resistance Report for Mycobacterium Abscessus Group by Whole-Genome Sequencing. A Tool for Appropriate Antimicrobial Therapy",
          type: "Poster",
          presenter: "Navas, Daniel",
          organisation: "AdventHealth Orlando",
          formattedDateTime: "Sunday, June 7, 10:00 AM – 4:00 PM ET"
        },
        {
          title: "Validation of a New Candida Auris Strain Typing Protocol Using Next-Generation Sequencing",
          type: "Poster",
          presenter: "Simmons-Williams, Caroline",
          organisation: "Tampa General Hospital",
          formattedDateTime: "Saturday, June 6, 10:00 AM – 5:00 PM ET"
        },
        {
          title: "Whole Genome Sequencing Revealed a Two-Amino Acid Deletion in the Omega Loop of KPC-4 Conferring Resistance to Ceftazidime-Avibactam and Loss of Carbapenemase Activity in Klebsiella Michiganensis",
          type: "Poster",
          presenter: "Zhou, Chunyi",
          organisation: "University of Nebraska Medical Center",
          formattedDateTime: "Saturday, June 6, 10:00 AM – 5:00 PM ET"
        },
        {
          title: "Replacing Pulsed Field Gel Electrophoresis in the Clinical Microbiology Laboratory: Whole Genomic Sequencing Using the Illumina and Nanopore Platforms versus Fourier IR Mass Spectroscopy - SeqIR",
          type: "Poster",
          presenter: "Desai, Karan",
          organisation: "Mount Sinai Hospital",
          formattedDateTime: "Saturday, June 6, 10:00 AM – 5:00 PM ET"
        },
        {
          title: "Automating Targeted Next-Generation Sequencing-Based Pathogen Identification and Genotyping Assays",
          type: "Poster",
          presenter: "Uhteg, Kate",
          organisation: "Johns Hopkins Hospital",
          formattedDateTime: "Saturday, June 6, 10:00 AM – 5:00 PM ET"
        },
        {
          title: "Next Generation Sequencing of Highly Resistant NDM-Producing Enterobacterales Isolated Tested at Adventhealth Orlando",
          type: "Poster",
          presenter: "Hein, Taylor",
          organisation: "AdventHealth Orlando",
          formattedDateTime: "Saturday, June 6, 10:00 AM – 5:00 PM ET"
        },
        {
          title: "Detection of Blaz Type and Regulatory Mutations to Predict the Inoculum Effect in Mssa Bacteremia and Guide Rapid Dual β-Lactam Escalation: A Feasibility Study",
          type: "Poster",
          presenter: "Alexander, José",
          organisation: "AdventHealth Orlando",
          formattedDateTime: "Saturday, June 6, 10:00 AM – 5:00 PM ET"
        }
      ]
    }
  }
};
</script>

<style scoped>
.home-section-title {
  font-size: 2em;
  font-weight: 300;
  margin-bottom: 1em;
}
</style>
